import { Box, styled, useTheme } from '@mui/material';
import { memo } from 'react';

import {
  GlowField,
  capitalizeWords,
  getIsItalyRegionId,
  parseItalianState,
} from '#shared';

import type { NewCase } from '../../types';

interface NewCaseCardContentCompactProps {
  data: NewCase & {
    updatedFields?: string[];
    isNewOnBoard?: boolean;
  };
  highlightDuration?: number;
  width?: number;
}

const PREFIX = 'TSMED-NewCaseCardContentCompact';

const newCaseCardContentCompactClasses = {
  from: `${PREFIX}-from`,
  city: `${PREFIX}-city`,
  state: `${PREFIX}-state`,
  txCode: `${PREFIX}-txCode`,
};

interface RootProps {
  highlightBorder?: boolean;
  isActiveStatus?: boolean;
}

const Root = styled(Box, {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
  shouldForwardProp: prop =>
    prop !== 'highlightBorder' && prop !== 'isActiveStatus',
})<RootProps>(({ theme: { palette, spacing, utils } }) => ({
  display: 'flex',
  fontSize: utils.clamp(12, 14),
  gap: spacing(1),
  [`.${newCaseCardContentCompactClasses.from}`]: {
    color: palette.grey[100],
    display: 'flex',
    minWidth: 0,
    gap: spacing(0.5),

    [`.${newCaseCardContentCompactClasses.city}`]: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      position: 'relative',
      paddingRight: spacing(1),
      ['&:after']: {
        position: 'absolute',
        content: '""',
        display: 'block',
        width: 5,
        height: '100%',
        right: 0,
        top: 0,
        background:
          'linear-gradient(90deg, rgba(15, 21, 28, 0.00) 0%, #0F151C 100%)',
      },
    },
    [`.${newCaseCardContentCompactClasses.state}`]: {},
  },
  [`.${newCaseCardContentCompactClasses.txCode}`]: {
    color: 'var(--primaryColor)',
    flexGrow: 1,
    whiteSpace: 'nowrap',
    textAlign: 'right',
  },
}));

export const NewCaseCardContentCompact = memo<NewCaseCardContentCompactProps>(
  ({ data: newCase, highlightDuration }) => {
    const [city, state] = newCase.donorHospital?.address.split(', ') ?? [
      '---',
      '---',
    ];
    const txHospitalCode = newCase.transplantCenterCode ?? '---';
    const { palette } = useTheme();
    const isItalian = getIsItalyRegionId(newCase.serviceRegionId ?? '');
    const stateToRender = isItalian
      ? parseItalianState(state)
      : state.toUpperCase();

    return (
      <Root>
        <Box className={newCaseCardContentCompactClasses.from}>
          <Box className={newCaseCardContentCompactClasses.city}>
            <GlowField
              component={'span'}
              startColor={palette.grey[100]}
              deps={[city, stateToRender]}
              ms={highlightDuration}
            >
              {capitalizeWords(city)},
            </GlowField>
          </Box>
          <Box className={newCaseCardContentCompactClasses.state}>
            <GlowField
              component={'span'}
              startColor={palette.grey[100]}
              deps={[city, stateToRender]}
              ms={highlightDuration}
            >
              {stateToRender}
            </GlowField>
          </Box>
        </Box>
        <GlowField
          startColor={palette.orange[500]}
          deps={[txHospitalCode]}
          className={newCaseCardContentCompactClasses.txCode}
          ms={highlightDuration}
        >
          {txHospitalCode}
        </GlowField>
      </Root>
    );
  },
);

NewCaseCardContentCompact.displayName = 'NewCaseCardContent';
